import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Message, ActiveToolCall, RichContent } from './types';
import { streamChatMessage, createSession, fetchAgents } from '../../../../infra/api/askFreya/chatService';
import type { ToolCallEvent, UiBlockEvent, AgentInfo } from '../../../../infra/api/askFreya/types';
import { getAuthToken } from '../../../../infra/api/askFreya/getAuthToken';

interface AskFreyaContextType {
  messages: Message[];
  isLoading: boolean;
  sendMessage: (text: string) => Promise<void>;
  startNewChat: () => void;
  sessionId: string | null;
  error: string | null;
  agents: AgentInfo[];
  selectedAgent: string | null;
  setSelectedAgent: (agentName: string) => void;
}

const AskFreyaContext = createContext<AskFreyaContextType | undefined>(undefined);

interface AskFreyaProviderProps {
  children: ReactNode;
}

export const AskFreyaProvider: React.FC<AskFreyaProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const token = await getAuthToken();
        const agentList = await fetchAgents(token);
        setAgents(agentList);
        if (agentList.length > 0 && !selectedAgent) {
          setSelectedAgent(agentList[0].name);
        }
      } catch (err) {
        console.error('[AskFreya] Failed to load agents:', err);
        setError(err instanceof Error ? err.message : 'Failed to load agents');
      }
    };
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!selectedAgent) return;
      const agent = selectedAgent;
      if (!text.trim()) return;

      setIsLoading(true);
      setError(null);

      const userMessage: Message = {
        id: crypto.randomUUID(),
        sender: 'user',
        text,
        timestamp: new Date(),
        contentBlocks: [{ type: 'text', text }],
      };

      const thinkingId = crypto.randomUUID();
      const thinkingMessage: Message = {
        id: thinkingId,
        sender: 'bot',
        text: '',
        timestamp: new Date(),
        isThinking: true,
        contentBlocks: [],
      };

      setMessages((prev) => [...prev, userMessage, thinkingMessage]);

      try {
        const token = await getAuthToken();

        let currentSessionId = sessionIdRef.current;
        if (!currentSessionId) {
          const session = await createSession({ agent_name: agent }, token);
          currentSessionId = session.id;
          sessionIdRef.current = currentSessionId;
          setSessionId(currentSessionId);
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        await streamChatMessage(
          { message: text, session_id: currentSessionId, agent_name: agent },
          token,
          {
            onToken: ({ content }) => {
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== thinkingId) return msg;
                  const blocks = msg.contentBlocks;
                  const lastBlock = blocks[blocks.length - 1];
                  const updatedBlocks =
                    lastBlock && lastBlock.type === 'text'
                      ? [...blocks.slice(0, -1), { ...lastBlock, text: lastBlock.text + content }]
                      : [...blocks, { type: 'text' as const, text: content }];
                  return { ...msg, text: msg.text + content, contentBlocks: updatedBlocks };
                })
              );
            },
            onDone: ({ message_id }) => {
              setMessages((prev) => prev.map((msg) => (msg.id === thinkingId ? { ...msg, isThinking: false, messageId: message_id } : msg)));
              setIsLoading(false);
            },
            onError: (err) => {
              setMessages((prev) => prev.map((msg) => (msg.id === thinkingId ? { ...msg, text: 'Sorry, an error occurred.', isThinking: false } : msg)));
              setError(err.message);
              setIsLoading(false);
            },
            onToolCall: (event: ToolCallEvent) => {
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== thinkingId) return msg;
                  const existing = msg.toolCalls ?? [];
                  if (event.status === 'completed') {
                    const updatedToolCalls = existing.map((tc) => (tc.tool_call_id === event.tool_call_id ? { ...tc, status: 'completed' as const } : tc));
                    const updatedBlocks = msg.contentBlocks.map((block) =>
                      block.type === 'tool_call' && block.toolCall.tool_call_id === event.tool_call_id
                        ? { ...block, toolCall: { ...block.toolCall, status: 'completed' as const } }
                        : block
                    );
                    return { ...msg, toolCalls: updatedToolCalls, contentBlocks: updatedBlocks };
                  }
                  const newCall: ActiveToolCall = {
                    tool_call_id: event.tool_call_id,
                    display_name: event.display_name,
                    category: event.category,
                    status: event.status,
                    description: event.description,
                  };
                  return {
                    ...msg,
                    toolCalls: [...existing, newCall],
                    contentBlocks: [...msg.contentBlocks, { type: 'tool_call' as const, toolCall: newCall }],
                  };
                })
              );
            },
            onThinking: ({ content }) => {
              setMessages((prev) => prev.map((msg) => (msg.id === thinkingId ? { ...msg, thinkingContent: (msg.thinkingContent ?? '') + content } : msg)));
            },
            onUiBlock: (event: UiBlockEvent) => {
              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== thinkingId) return msg;
                  const block: RichContent = {
                    type: event.type,
                    stats: event.stats,
                    table: event.table,
                    list: event.list,
                    action: event.action,
                  };
                  return {
                    ...msg,
                    richContent: [...(msg.richContent ?? []), block],
                    contentBlocks: [...msg.contentBlocks, { type: 'rich' as const, content: block }],
                  };
                })
              );
            },
          },
          controller.signal
        );
      } catch (err) {
        console.error('[AskFreya] sendMessage error:', err);
        setMessages((prev) => prev.map((msg) => (msg.id === thinkingId ? { ...msg, text: 'Unable to send the message.', isThinking: false } : msg)));
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    },
    [selectedAgent]
  );

  const startNewChat = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    sessionIdRef.current = null;
    setSessionId(null);
    setError(null);
    setIsLoading(false);
  }, []);

  const value: AskFreyaContextType = {
    messages,
    isLoading,
    sendMessage,
    startNewChat,
    sessionId,
    error,
    agents,
    selectedAgent,
    setSelectedAgent,
  };

  return <AskFreyaContext.Provider value={value}>{children}</AskFreyaContext.Provider>;
};

export const useAskFreya = (): AskFreyaContextType => {
  const context = useContext(AskFreyaContext);
  if (context === undefined) {
    throw new Error('useAskFreya must be used within an AskFreyaProvider');
  }
  return context;
};
